import {
  AI_CONTRACT_VERSION,
  discoverMeaningsResultSchema,
  generateMeaningPackagesResultSchema,
  type DiscoverMeaningsRequest,
  type DiscoverMeaningsResult,
  type GenerateMeaningPackagesRequest,
  type GenerateMeaningPackagesResult,
  type MeaningCandidate,
  type MeaningPackage,
  type SelectedSense,
} from "../../../contracts/ai/schemas.ts";
import type { AiCallOptions, VocabularyAi } from "../../../application/ports/ai.ts";
import type { WordType } from "../../../domain/vocabulary/types.ts";
import type { Exercise } from "../../../domain/vocabulary/types.ts";
import { ProductError } from "../../../contracts/errors.ts";

interface CatalogSense {
  id: string;
  type: WordType;
  shortLabel: string;
  definition: string;
  example: string;
  hint: string | null;
  patterns: string[];
  memory: string | null;
}

const CATALOG: Record<string, CatalogSense[]> = {
  book: [
    {
      id: "book-read",
      type: "noun",
      shortLabel: "A written work you read",
      definition: "A set of written or printed pages that tells a story or gives information.",
      example: "I borrowed a book about space from the library.",
      hint: "книга",
      patterns: ["read a book", "a book about"],
      memory: "A book opens a new world.",
    },
    {
      id: "book-reserve",
      type: "verb",
      shortLabel: "Reserve something",
      definition: "To arrange to have a room, ticket, table, or service at a particular time.",
      example: "We need to book a table for Friday evening.",
      hint: "бронировать",
      patterns: ["book a table", "book a flight", "book in advance"],
      memory: "Book it today. Enjoy it tomorrow.",
    },
    {
      id: "book-records",
      type: "noun",
      shortLabel: "Financial records",
      definition: "The written records of the money a business receives and spends, usually called “the books”.",
      example: "The accountant checks the books every month.",
      hint: "бухгалтерские книги",
      patterns: ["keep the books", "check the books"],
      memory: "The books keep the business numbers.",
    },
  ],
  run: [
    {
      id: "run-move",
      type: "verb",
      shortLabel: "Move quickly on foot",
      definition: "To move on your feet faster than walking.",
      example: "I run in the park every morning.",
      hint: "бегать",
      patterns: ["run fast", "run a marathon"],
      memory: "Two feet, a faster beat.",
    },
    {
      id: "run-manage",
      type: "verb",
      shortLabel: "Manage a business or activity",
      definition: "To be in charge of a business, organization, or activity.",
      example: "They run a small café near the station.",
      hint: "управлять, руководить",
      patterns: ["run a business", "run a café"],
      memory: "You run the café, even standing still.",
    },
  ],
  seep: [
    {
      id: "seep-flow",
      type: "verb",
      shortLabel: "Pass slowly through small openings",
      definition: "To flow slowly through small openings or through a material.",
      example: "Water began to seep through the wall.",
      hint: "просачиваться",
      patterns: ["seep through", "seep into"],
      memory: "SEEP = SLIP through small gaps.",
    },
  ],
  percolate: [
    {
      id: "percolate-liquid",
      type: "verb",
      shortLabel: "Filter slowly through a material",
      definition: "For a liquid to pass slowly through a material with very small spaces in it.",
      example: "Rainwater can percolate through the soil.",
      hint: "просачиваться сквозь",
      patterns: ["percolate through", "percolate into"],
      memory: "Water takes the tiny paths through soil.",
    },
    {
      id: "percolate-idea",
      type: "verb",
      shortLabel: "Spread or develop gradually",
      definition: "For an idea or information to spread or develop slowly.",
      example: "The idea began to percolate through the team.",
      hint: "постепенно распространяться",
      patterns: ["ideas percolate", "percolate through"],
      memory: "An idea slowly finds its way.",
    },
  ],
  charge: [
    {
      id: "charge-price",
      type: "verb",
      shortLabel: "Ask someone to pay",
      definition: "To ask a particular amount of money for a product or service.",
      example: "They charge ten pounds for delivery.",
      hint: "взимать плату",
      patterns: ["charge for delivery", "charge a fee"],
      memory: "A service comes with a charge.",
    },
    {
      id: "charge-battery",
      type: "verb",
      shortLabel: "Put electricity into a battery",
      definition: "To put electrical energy into a battery so it can be used again.",
      example: "I need to charge my phone before we leave.",
      hint: "заряжать",
      patterns: ["charge your phone", "fully charged"],
      memory: "Charge your phone; fill its energy tank.",
    },
    {
      id: "charge-fee",
      type: "noun",
      shortLabel: "An amount of money to pay",
      definition: "An amount of money you must pay for a service.",
      example: "There is an extra charge for delivery.",
      hint: "плата",
      patterns: ["an extra charge", "a delivery charge"],
      memory: "The extra charge is the extra cost.",
    },
    {
      id: "charge-rush",
      type: "verb",
      shortLabel: "Rush forward",
      definition: "To move forward quickly and forcefully towards someone or something.",
      example: "The players charge onto the field.",
      hint: "стремительно бросаться вперёд",
      patterns: ["charge towards", "charge onto"],
      memory: "A sudden rush forward.",
    },
  ],
  "look after": [
    {
      id: "look-after-care",
      type: "phrasal_verb",
      shortLabel: "Take care of someone or something",
      definition: "To be responsible for someone or something and make sure they are safe or well.",
      example: "Could you look after my plants while I am away?",
      hint: "присматривать, ухаживать",
      patterns: ["look after children", "look after a pet"],
      memory: "Look after = keep them safe.",
    },
  ],
};

const CONTEXT_QUESTIONS: Array<(word: string, sense: CatalogSense) => string> = [
  (word, sense) =>
    `Hiking notes from Cumbria left a blank: they decided to ___ before sunset, in the sense “${sense.shortLabel}” (${word}).`,
  (_word, sense) =>
    `A Bristol theatre prompt asks the actor to ___ during the kitchen scene about ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `At a Glasgow library desk the student needed to ___ because of this idea: ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `During a Cardiff football drill the coach shouted to ___ in a way that matches “${sense.shortLabel}”.`,
  (_word, sense) =>
    `On the overnight train to Aberdeen someone tried to ___ while discussing ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `A Brighton café chalkboard told guests not to forget to ___ — it refers to ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `In a York biology lab the diagram showed how things ___ , matching ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `The Edinburgh hotel clerk whispered that visitors should ___ if they want ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `Grandma’s postcard from Cornwall said she would ___ every Sunday, meaning ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `A Belfast news headline hinted people might ___ after the storm, for ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `A Dublin travel blogger wrote: “Remember to ___ at dawn,” using ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `In an Oxford office thread they agreed to ___ by Friday, for ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `The detective’s Liverpool notebook said the suspect would ___ near the docks, tied to ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `A Bath poetry line asked the reader to ___ through silence, echoing ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `The Manchester app tutorial, step four, tells you to ___ when you need ${sense.shortLabel.toLowerCase()}.`,
  (_word, sense) =>
    `Farmers in a Norfolk documentary had to ___ before winter, illustrating ${sense.shortLabel.toLowerCase()}.`,
];

const MEANING_QUESTIONS: Array<(sense: CatalogSense) => string> = [
  (sense) => `Give the English headword for this definition: ${sense.definition}`,
  (sense) => `Name the ${sense.type.replaceAll("_", " ")} whose short label is “${sense.shortLabel}”.`,
  (sense) => `From this learner gloss only, write the word — ${sense.definition}`,
  (sense) => `Which vocabulary item do learners use when they talk about ${sense.shortLabel.toLowerCase()}?`,
];

function genericSenses(word: string): CatalogSense[] {
  return [
    {
      id: `${word}-core`,
      type: word.includes(" ") ? "phrasal_verb" : "verb",
      shortLabel: `Core learner meaning of ${word}`,
      definition: `A common, useful way English speakers use “${word}” in everyday situations.`,
      example: `People often use ${word} in a clear, everyday sentence.`,
      hint: null,
      patterns: [`use ${word}`, `${word} carefully`],
      memory: null,
    },
    {
      id: `${word}-noun`,
      type: "noun",
      shortLabel: `A thing or idea called ${word}`,
      definition: `A noun sense of “${word}” naming a thing, idea, or event.`,
      example: `We talked about the ${word} for a long time.`,
      hint: null,
      patterns: [`the ${word}`, `a ${word}`],
      memory: null,
    },
    {
      id: `${word}-figurative`,
      type: "verb",
      shortLabel: `A figurative use of ${word}`,
      definition: `A less literal way to use “${word}” in conversation or writing.`,
      example: `The idea seemed to ${word} through the conversation.`,
      hint: null,
      patterns: [`${word} through`],
      memory: null,
    },
  ];
}

function matchesContext(sense: CatalogSense, context?: string): boolean {
  if (!context) {
    return false;
  }
  const c = context.toLowerCase();
  return (
    (sense.id === "charge-battery" && /phone|battery/.test(c)) ||
    (sense.id === "charge-price" && /delivery|pounds|fee/.test(c)) ||
    (sense.id === "book-reserve" && /table|hotel|flight/.test(c)) ||
    (sense.id === "percolate-idea" && /idea|team/.test(c)) ||
    (sense.id === "percolate-liquid" && /soil|coffee|water/.test(c)) ||
    sense.shortLabel.toLowerCase().split(" ").some((token) => token.length > 4 && c.includes(token))
  );
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function distractorLabel(word: string, preferred?: string): string {
  if (preferred && preferred.trim().toLowerCase() !== word.toLowerCase()) {
    return preferred;
  }
  const fallbacks = ["trickle", "charge", "seep", "percolate", "reserve"];
  return fallbacks.find((item) => item !== word.toLowerCase()) ?? `${word}-alt`;
}

function buildExercises(
  word: string,
  sense: CatalogSense,
  itemId: string,
  relations: MeaningPackage["relations"],
  comparison: GenerateMeaningPackagesRequest["comparisonIndex"],
): Exercise[] {
  const hasRelations = relations.length > 0;
  const contextCount = hasRelations ? 14 : 16;
  const exercises: Exercise[] = [];
  for (let i = 0; i < contextCount; i += 1) {
    const question =
      CONTEXT_QUESTIONS[i]?.(word, sense) ??
      `Unique recall drill ${i} from ${["Lisbon", "Oslo", "Prague", "Vienna"][i % 4]} asks you to ___ for “${sense.shortLabel}”.`;
    exercises.push({
      id: `${itemId}-cr-${i}`,
      kind: "context_recall",
      question,
      acceptedAnswers: [word],
      correctAnswer: word,
      explanation: `The sentence needs “${word}” in the sense: ${sense.shortLabel}.`,
      referencedMeaningIds: [],
      relatedWordDistinctions: [],
    });
  }
  for (let i = 0; i < 4; i += 1) {
    exercises.push({
      id: `${itemId}-mw-${i}`,
      kind: "meaning_to_word",
      question: MEANING_QUESTIONS[i]?.(sense) ?? `Identify the word for “${sense.shortLabel}” (prompt ${i}).`,
      acceptedAnswers: [word],
      correctAnswer: word,
      explanation: `${word} is the ${sense.type.replaceAll("_", " ")} that means “${sense.shortLabel}”.`,
      referencedMeaningIds: [],
      relatedWordDistinctions: [],
    });
  }
  if (hasRelations) {
    for (let i = 0; i < 2; i += 1) {
      const relation = relations[i % relations.length];
      const target = comparison.find((item) => item.meaningId === relation.targetMeaningId);
      const other = distractorLabel(word, target?.word);
      const options = [
        { id: `${itemId}-opt-${i}-a`, label: word },
        { id: `${itemId}-opt-${i}-b`, label: other },
      ];
      const pattern = new RegExp(escapeRegExp(word), "i");
      const blanked = pattern.test(sense.example)
        ? sense.example.replace(pattern, "____")
        : `People often ____ when they mean “${sense.shortLabel}”.`;
      exercises.push({
        id: `${itemId}-cf-${i}`,
        kind: "confusable_context",
        question:
          i === 0
            ? `Choose the best word in this example: ${blanked}`
            : `Which word fits this distinction versus “${target?.word ?? other}”: ${relation.explanation}`,
        acceptedAnswers: [word],
        correctAnswer: word,
        explanation: relation.explanation,
        referencedMeaningIds: [relation.targetMeaningId],
        relatedWordDistinctions: [
          { meaningId: relation.targetMeaningId, explanation: relation.explanation },
        ],
        options,
        correctOptionId: options[0].id,
      });
    }
  }
  return exercises;
}

function toCandidate(
  sense: CatalogSense,
  request: DiscoverMeaningsRequest,
): MeaningCandidate {
  const saved = request.existingSenses.find(
    (item) =>
      item.shortLabel.toLowerCase() === sense.shortLabel.toLowerCase() ||
      item.definition.toLowerCase() === sense.definition.toLowerCase(),
  );
  return {
    id: sense.id,
    type: sense.type,
    shortLabel: sense.shortLabel,
    definition: sense.definition,
    example: sense.example,
    match: saved ? { kind: "saved", existingMeaningId: saved.meaningId } : { kind: "new" },
    contextMatch: matchesContext(sense, request.context),
  };
}

function packageFromSense(
  word: string,
  sense: CatalogSense,
  selected: SelectedSense,
  request: GenerateMeaningPackagesRequest,
): MeaningPackage {
  const usefulComparisons = request.comparisonIndex
    .filter((item) => item.word.trim().toLowerCase() !== word.toLowerCase())
    .slice(0, 2);
  const relations = usefulComparisons.map((item) => ({
    targetMeaningId: item.meaningId,
    explanation: `“${word}” (${sense.shortLabel.toLowerCase()}) is not the same as “${item.word}” (${item.shortLabel.toLowerCase()}).`,
  }));
  return {
    itemId: selected.itemId,
    type: selected.type,
    shortLabel: sense.shortLabel,
    definition: sense.definition,
    russianHint: sense.hint,
    example: sense.example,
    memoryKey: sense.memory,
    collocations: sense.patterns.slice(0, 3),
    relations,
    exercises: buildExercises(word, { ...sense, type: selected.type }, selected.itemId, relations, request.comparisonIndex),
    reviewNote:
      selected.sourceType && selected.sourceType !== selected.type
        ? `Type was kept as ${selected.type}.`
        : null,
  };
}

export class FakeVocabularyAi implements VocabularyAi {
  failDiscover = false;
  failGenerateFor = new Set<string>();

  async discoverMeanings(
    request: DiscoverMeaningsRequest,
    options: AiCallOptions,
  ): Promise<DiscoverMeaningsResult> {
    this.guard(options);
    options.onProgress?.({
      requestId: request.requestId,
      stage: "discovering",
      completedItems: 0,
      totalItems: 1,
      message: "Finding useful meanings…",
    });
    if (this.failDiscover) {
      throw new ProductError("generation_failed", "Discovery failed.", true);
    }
    const senses = CATALOG[request.normalizedWord] ?? genericSenses(request.normalizedWord);
    const remaining = senses.filter((sense) => !request.previouslyShownCandidateIds.includes(sense.id));
    const initial = request.previouslyShownCandidateIds.length ? remaining : remaining.slice(0, 5);
    return discoverMeaningsResultSchema.parse({
      contractVersion: AI_CONTRACT_VERSION,
      requestId: request.requestId,
      candidates: initial.map((sense) => toCandidate(sense, request)),
      corrections: [],
      moreAvailable: remaining.length > initial.length,
    });
  }

  async generateMeaningPackages(
    request: GenerateMeaningPackagesRequest,
    options: AiCallOptions,
  ): Promise<GenerateMeaningPackagesResult> {
    this.guard(options);
    const outcomes = request.senses.map((selected, index) => {
      options.onProgress?.({
        requestId: request.requestId,
        itemId: selected.itemId,
        stage: "generating",
        completedItems: index,
        totalItems: request.senses.length,
        message: `Creating a card for “${selected.shortLabel}”…`,
      });
      if (this.failGenerateFor.has(selected.itemId) || this.failGenerateFor.has(selected.candidateId)) {
        return {
          itemId: selected.itemId,
          status: "failed" as const,
          code: "generation_failed",
          message: "This meaning could not be generated.",
        };
      }
      const catalog = (CATALOG[request.normalizedWord] ?? genericSenses(request.normalizedWord)).find(
        (sense) => sense.id === selected.candidateId,
      ) ?? {
        id: selected.candidateId,
        type: selected.type,
        shortLabel: selected.shortLabel,
        definition: selected.definition,
        example: selected.example,
        hint: selected.sourceHint ?? null,
        patterns: [],
        memory: null,
      };
      const pack = packageFromSense(request.normalizedWord, catalog, selected, request);
      return { itemId: selected.itemId, status: "ready" as const, package: pack };
    });
    return generateMeaningPackagesResultSchema.parse({
      contractVersion: AI_CONTRACT_VERSION,
      requestId: request.requestId,
      outcomes,
    });
  }

  private guard(options: AiCallOptions): void {
    if (options.signal?.aborted) {
      throw new ProductError("cancelled", "Generation was cancelled.");
    }
  }
}
